import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Bot,
  BookOpen,
  Phone,
  Users,
  History,
  BarChart3,
  LogOut,
  Settings,
  Plus,
  ChevronRight,
  Wallet,
  Lock,
  Activity,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { useCampaignAccess } from "@/hooks/use-workspace-subscription";
import { useWorkspaceConcurrency } from "@/services/call.query";
import verbeoLogo from "@/assets/verbeo-logo.svg";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const menuItems = [
  { title: "Agents", url: "/agents", icon: Bot },
  { title: "Knowledge Base", url: "/knowledge", icon: BookOpen },
  { title: "Phone Numbers", url: "/phone-numbers", icon: Phone },
  { title: "Campaign", url: "/batch-call", icon: Users, requiresSubscription: true },
  { title: "Call History", url: "/call-history", icon: History },
  { title: "Analytics", url: "/analytics", icon: BarChart3 },
  { title: "Billing", url: "/billing", icon: Wallet },
];

const secondaryItems = [
  { title: "Settings", url: "/settings", icon: Settings },
];

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspaceId, setCurrentWorkspaceId, isLoading: isLoadingWorkspace } = useWorkspace();
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useWorkspaces();
  const { hasAccess: hasCampaignAccess, isLoading: isLoadingSubscription } = useCampaignAccess();

  // Live concurrency usage for the footer widget: active calls vs the
  // server-enforced cap (workspaces.call_concurrency_limit). The hook polls
  // every 5s — realtime isn't wired for call_logs.
  const { data: concurrency, isLoading: isLoadingConcurrency } =
    useWorkspaceConcurrency(currentWorkspaceId || undefined);
  const activeCalls = concurrency?.active_calls ?? 0;
  const concurrencyLimit = concurrency?.concurrency_limit ?? 0;
  const atCapacity = concurrencyLimit > 0 && activeCalls >= concurrencyLimit;
  const concurrencyPct =
    concurrencyLimit > 0 ? Math.min(100, (activeCalls / concurrencyLimit) * 100) : 0;

  // Find current workspace name for display
  const currentWorkspace = workspaces?.find(w => w.id === currentWorkspaceId);

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  // Helper to determine if a route is active (simple includes check for nested routes)
  const isRouteActive = (itemUrl: string) => {
    if (!currentWorkspaceId) return false;
    const fullUrl = `/${currentWorkspaceId}${itemUrl}`;
    
    // Handle root/agents redirect case
    if (itemUrl === "/agents") {
      return location.pathname === fullUrl || location.pathname === `/${currentWorkspaceId}`;
    }
    return location.pathname.startsWith(fullUrl);
  };

  // Check if menu item should be locked
  const isItemLocked = (item: typeof menuItems[0]) => {
    if (!item.requiresSubscription) return false;
    if (isLoadingSubscription) return false;
    return !hasCampaignAccess;
  };

  return (
    <Sidebar collapsible="none" className="border-r border-border/50 w-72 bg-gradient-to-b from-card to-background shadow-xl z-20 font-sans">
      <SidebarHeader className="p-4 pb-0 mb-1">
        <div className="flex items-center gap-3 mb-2 px-1">
          <div className="p-2 rounded-xl bg-gradient-to-br from-primary/20 to-purple-600/10 border border-white/10 shadow-sm">
            <img src={verbeoLogo} alt="Verbeo AI" className="h-7 w-auto" />
          </div>
          <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 tracking-tight">
            Verbeo AI
          </span>
        </div>
        
        {/* Workspace Selector with Loading State */}
        <div className="flex items-center gap-2 mb-1 bg-muted/30 p-1 rounded-lg border border-border/40 shadow-sm">
          {isLoadingWorkspaces || isLoadingWorkspace ? (
            <Skeleton className="flex-1 h-9 rounded-md" />
          ) : workspaces && workspaces.length > 0 ? (
            <Select
              value={currentWorkspace ? currentWorkspaceId! : workspaces[0]?.id}
              onValueChange={setCurrentWorkspaceId}
            >
              <SelectTrigger className="flex-1 border-0 bg-transparent shadow-none hover:bg-background/50 h-9 focus:ring-0 text-sm font-medium">
                <SelectValue placeholder="Select workspace">
                  {currentWorkspace?.name || workspaces[0]?.name || "Select workspace"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((workspace) => (
                  <SelectItem key={workspace.id} value={workspace.id} className="font-medium">
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex-1 px-3 py-2 text-sm text-muted-foreground">
              No workspaces available
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(currentWorkspaceId ? `/${currentWorkspaceId}/settings` : "/settings")}
            className="shrink-0 h-8 w-8 text-muted-foreground hover:text-primary rounded-md"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3 py-1 scrollbar-none">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground/80 px-4 mb-1 mt-1">
            Platform
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0.5">
              {menuItems.map((item) => {
                const isActive = isRouteActive(item.url);
                const isLocked = isItemLocked(item);
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild tooltip={item.title} className="rounded-lg h-auto py-0.5">
                            <NavLink
                              to={currentWorkspaceId ? `/${currentWorkspaceId}${item.url}` : "#"}
                              onClick={(e) => {
                                if (!currentWorkspaceId || isLocked) {
                                  e.preventDefault();
                                }
                              }}
                              className={({ isActive: linkActive }) =>
                                `flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 group relative overflow-hidden ${
                                  isLocked || !currentWorkspaceId
                                    ? "text-muted-foreground/50 cursor-not-allowed"
                                    : linkActive
                                    ? "bg-gradient-to-r from-primary/15 to-primary/5 text-primary font-semibold shadow-sm"
                                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground font-medium"
                                }`
                              }
                            >
                              {({ isActive: linkActive }) => (
                                <>
                                  {linkActive && !isLocked && currentWorkspaceId && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-full" />
                                  )}
                                  <item.icon className={`w-5 h-5 transition-transform duration-200 ${
                                    isLocked || !currentWorkspaceId
                                      ? 'opacity-50' 
                                      : linkActive 
                                      ? 'scale-110 text-primary' 
                                      : 'group-hover:scale-105 group-hover:text-foreground'
                                  }`} />
                                  <span className="flex-1 text-base">{item.title}</span>
                                  {isLocked ? (
                                    <Lock className="w-4 h-4 opacity-50" />
                                  ) : linkActive ? (
                                    <ChevronRight className="w-4 h-4 opacity-50" />
                                  ) : null}
                                </>
                              )}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isLocked && (
                          <TooltipContent side="right">
                            <p>Pro subscription required</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="my-2 mx-4 opacity-50" />

        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground/80 px-4 mb-1">
            Configuration
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0.5">
              {secondaryItems.map((item) => {
                const isActive = isRouteActive(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title} className="rounded-lg h-auto py-0.5">
                      <NavLink
                        to={currentWorkspaceId ? `/${currentWorkspaceId}${item.url}` : "#"}
                        onClick={(e) => {
                          if (!currentWorkspaceId) {
                            e.preventDefault();
                          }
                        }}
                        className={({ isActive: linkActive }) =>
                          `flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 group ${
                            !currentWorkspaceId
                              ? "text-muted-foreground/50 cursor-not-allowed"
                              : linkActive
                              ? "bg-gradient-to-r from-primary/15 to-primary/5 text-primary font-semibold shadow-sm"
                              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground font-medium"
                          }`
                        }
                      >
                        {({ isActive: linkActive }) => (
                          <>
                            <item.icon className={`w-5 h-5 transition-transform duration-200 ${linkActive ? 'scale-110 text-primary' : 'group-hover:scale-105 group-hover:text-foreground'}`} />
                            <span className="flex-1 text-base">{item.title}</span>
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2 border-t border-border/40 bg-muted/10 backdrop-blur-sm mt-auto">
        {/* Live concurrency usage — active calls vs the workspace's enforced cap */}
        {currentWorkspaceId && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="mb-1 px-3 py-2.5 rounded-xl bg-muted/30 border border-border/40 shadow-sm cursor-default">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Activity className={`h-3.5 w-3.5 shrink-0 ${atCapacity ? "text-destructive" : "text-primary"}`} />
                      <span className="text-xs font-semibold text-muted-foreground truncate">Live calls</span>
                    </div>
                    {isLoadingConcurrency ? (
                      <Skeleton className="h-4 w-16 rounded" />
                    ) : (
                      <span className={`text-xs font-bold tabular-nums ${atCapacity ? "text-destructive" : "text-foreground"}`}>
                        {activeCalls}/{concurrencyLimit}{" "}
                        <span className="font-normal text-muted-foreground">used</span>
                      </span>
                    )}
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${atCapacity ? "bg-destructive" : "bg-primary"}`}
                      style={{ width: `${concurrencyPct}%` }}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>
                  {atCapacity
                    ? "At capacity — new calls will queue"
                    : `${activeCalls} of ${concurrencyLimit} concurrent calls in use`}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-background transition-all duration-200 border border-transparent hover:border-border/50 group cursor-pointer shadow-sm hover:shadow-md">
          <Avatar className="h-9 w-9 ring-2 ring-background shadow-sm transition-transform duration-200 group-hover:scale-105">
            <AvatarFallback className="bg-gradient-to-br from-primary to-purple-600 text-white font-bold text-sm">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate group-hover:text-primary transition-colors">{user?.email}</p>
            <p className="text-xs text-muted-foreground truncate font-medium">Admin Workspace</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSignOut}
            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
